import type { PeriodUsage, PriceListUsage, TariffPeriod, UserTariffRequest } from "../api/user";
import { formatPricelistPrice, formatSiteAmount, getPaymentPeriodLabel } from "./pricelistFormat";

const USAGE_REMAINDER_ORDER: TariffPeriod[] = ["day", "month", "year"];

const USAGE_REMAINDER_LABELS: Record<TariffPeriod, string> = {
  day: "Осталось на сегодня",
  month: "Осталось на месяц",
  year: "Осталось на год",
};

export interface TariffUsageRemainderRow {
  label: string;
  value: string;
}

const formatUsageRemainder = (usage: PeriodUsage): string | null => {
  if (usage.periodLimit == null) return null;

  const counter = usage.counter ?? 0;
  const limit = usage.periodLimit;

  if (counter >= limit) {
    return "Проверок не осталось";
  }

  const remaining = limit - counter;
  return `${remaining} из ${limit}`;
};

export const getTariffUsageRemainderRows = (tariff: PriceListUsage): TariffUsageRemainderRow[] => {
  const usageByPeriod = new Map<TariffPeriod, PeriodUsage>();

  for (const item of tariff.usage ?? []) {
    if (item.period && !usageByPeriod.has(item.period)) {
      usageByPeriod.set(item.period, item);
    }
  }

  return USAGE_REMAINDER_ORDER.flatMap((period) => {
    const usage = usageByPeriod.get(period);
    if (!usage) return [];

    const value = formatUsageRemainder(usage);
    if (value == null) return [];

    return [{ label: USAGE_REMAINDER_LABELS[period], value }];
  });
};

export const formatTariffDate = (value?: string): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ru-RU");
};

export const formatTariffPeriodRange = (tariff: PriceListUsage): string => {
  const start = formatTariffDate(tariff.startAt);
  const end = formatTariffDate(tariff.endAt);
  if (start === "—" && end === "—") return "—";
  return `${start} — ${end}`;
};

export const formatTariffPrice = (tariff: PriceListUsage): string => {
  if (tariff.tariffPrice == null) return "—";
  const periodLabel = tariff.tariffPeriod
    ? getPaymentPeriodLabel(tariff.tariffPeriod).toLowerCase()
    : "";
  const price = formatPricelistPrice(tariff.tariffPrice);
  return periodLabel ? `${price} / ${periodLabel}` : price;
};

export const formatTariffSiteAmount = (tariff: PriceListUsage): string =>
  formatSiteAmount(tariff.siteAmount);

export const formatTariffRequestPrice = (request: UserTariffRequest): string => {
  if (request.tariffPrice == null) return "—";
  const price = formatPricelistPrice(request.tariffPrice);
  if (request.tariffType === "checks" || !request.tariffPeriod) return price;
  const periodLabel = getPaymentPeriodLabel(request.tariffPeriod).toLowerCase();
  return `${price} / ${periodLabel}`;
};

export const formatTariffRequestAmount = (request: UserTariffRequest): string => {
  if (request.tariffType === "checks") {
    if (!request.checkPacks?.length) return "—";
    return request.checkPacks
      .map((pack) => {
        const count = pack.count ?? 0;
        return String(count);
      })
      .join(" + ");
  }
  const amount = request.tariffAmount ?? 1;
  if (!request.tariffPeriod) return String(amount);
  const periodLabel = getPaymentPeriodLabel(request.tariffPeriod).toLowerCase();
  return `${amount} × ${periodLabel}`;
};

export const formatTariffRequestType = (request: UserTariffRequest): string =>
  request.tariffType === "checks" ? "Докупка" : "Тариф";

export const formatTariffRequestTitle = (request: UserTariffRequest): string => {
  if (request.tariffType === "checks") {
    if (!request.checkPacks?.length) return "Докупка проверок";
    return request.checkPacks
      .map((pack) => {
        const typeLabel =
          pack.checkType === "siteCheck"
            ? "Сайты"
            : pack.checkType === "policyCheck"
              ? "Политика"
              : pack.checkType === "it"
                ? "Минцифры"
                : pack.checkType ?? "Проверки";
        return `${typeLabel}: ${pack.count ?? 0}`;
      })
      .join(", ");
  }
  return request.tariffName?.trim() || "—";
};
